import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import { UserAvatar } from "@components/ui/UserAvatar";
import { useDataStore } from "@store/user_org";
import { useState, useRef } from "react";
import { showNotification } from "context/notification.service";
import { usersApi } from "@services/api/users";
import Avvvatars from "avvvatars-react";

const SEED_COUNT = 60;

function buildSeeds(base: string): string[] {
  return [base, ...Array.from({ length: SEED_COUNT - 1 }, (_, i) => `${base}-${i + 2}`)];
}

export const ChangeUserPhotoModal = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const userData = useDataStore((state) => state.userData);
  const updateUserData = useDataStore((state) => state.updateUserData);
  const fetchUserData = useDataStore((state) => state.fetchUserData);
  const fetchOrgMembers = useDataStore((state) => state.fetchOrgMembers);

  const displayName = userData?.infos.display_name || "user";
  const seeds = buildSeeds(displayName);

  const [selectedPhoto, setSelectedPhoto] = useState<string>(
    userData?.customization.photo_url || "",
  );
  const [selectedSeed, setSelectedSeed] = useState<string>(
    userData?.customization.avatar_seed || displayName,
  );
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePickSeed = (seed: string) => {
    setSelectedSeed(seed);
    setSelectedPhoto("");
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !userData) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      showNotification({
        type: "error",
        message: "Type de fichier invalide. Formats acceptés : JPG, PNG, WEBP",
      });
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showNotification({
        type: "error",
        message: "La taille du fichier dépasse 5MB",
      });
      return;
    }

    setIsUploading(true);
    try {
      const result = await usersApi.uploadProfilePicture(userData.id, file);
      setSelectedPhoto(result.photo_url);
      setSelectedSeed("");
      showNotification({
        type: "success",
        message: "Photo téléchargée avec succès",
      });
    } catch (error) {
      console.error("Failed to upload profile picture:", error);
      showNotification({
        type: "error",
        message: "Échec du téléchargement de la photo",
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSave = async () => {
    if (!userData) return;

    setIsSaving(true);
    try {
      await updateUserData(userData.id, {
        customization: {
          ...userData.customization,
          photo_url: selectedPhoto,
          avatar_seed: selectedPhoto ? "" : selectedSeed,
        },
      });

      if (userData.org_id) {
        await fetchOrgMembers(userData.org_id);
      }
      await fetchUserData(userData.id);

      showNotification({
        type: "success",
        message: "Photo de profil mise à jour avec succès",
      });
      onClose();
    } catch (error) {
      console.error("Failed to update photo:", error);
      showNotification({
        type: "error",
        message: "Échec de la mise à jour de la photo",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <BaseModal
      title="Photo de profil"
      startIcon={<Icon1 name="image" size={20} />}
      onClose={onClose}
    >
      <div className="bg-neutral-100 flex flex-col gap-4 p-4">
        {/* Preview */}
        <div className="flex flex-col items-center gap-3">
          <UserAvatar
            src={selectedPhoto || null}
            value={selectedSeed || displayName}
            size={112}
            className="w-28 h-28 rounded-full border-2 border-neutral-200"
          />
        </div>

        {/* Avatar grid */}
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">Choisir un avatar</div>
          <div className="flex flex-row gap-2 flex-wrap">
            {seeds.map((seed) => {
              const isSelected = !selectedPhoto && selectedSeed === seed;
              return (
                <button
                  key={seed}
                  onClick={() => handlePickSeed(seed)}
                  className={`rounded-full border-2 transition-all overflow-hidden ${
                    isSelected
                      ? "border-blue-500 ring-2 ring-blue-200"
                      : "border-neutral-200 hover:border-neutral-400"
                  }`}
                >
                  <Avvvatars value={seed} style="shape" size={32} radius={0} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Upload */}
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h3 font-semibold text-black">ou télécharger une photo</div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileUpload}
            className="hidden"
          />
          <SecondaryButton
            startIcon={<Icon1 name="join" size={18} />}
            label={isUploading ? "Téléchargement..." : "Télécharger une photo"}
            variant="large"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
          />
          {selectedPhoto && (
            <SecondaryButton
              startIcon={<Icon1 name="trash" size={18} />}
              label="Utiliser l'avatar généré"
              variant="large"
              onClick={() => {
                setSelectedPhoto("");
                setSelectedSeed(userData?.customization.avatar_seed || displayName);
              }}
            />
          )}
        </div>
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={isSaving ? "Enregistrement..." : "Enregistrer"}
          onClick={handleSave}
          disabled={isSaving}
        />
      </div>
    </BaseModal>
  );
};
